import { useState, type ReactNode } from 'react';
import { Cpu, RefreshCw, Wifi, Battery, Thermometer, Signal, Clock, Terminal, FileText, RotateCw, Moon, Settings, Package, Image, Info } from 'lucide-react';
import { Card } from './Card';
import { Button } from './Button';
import { Input } from './Input';
import { Tabs } from './Tabs';
import { StatusBadge } from './StatusBadge';
import { PendingCommands } from './PendingCommands';
import { ScreenshotPanel } from './ScreenshotPanel';
import { LogsPanel } from './LogsPanel';
import { TimestampDisplay } from './TimestampDisplay';
import { CopyButton } from './CopyButton';
import { formatTimestamp, formatDelta } from '../lib/utils';
import type { ApiService } from '../lib/api';

/**
 * Copiable value cell for the device info grid.
 */
const Val = ({ children, copyValue, className = 'text-white' }: { children: ReactNode; copyValue?: string | null; className?: string }) => {
  const raw = copyValue ?? (typeof children === 'string' ? children : null);
  return (
    <span className={`group flex items-center gap-1 ${className}`}>
      {children}
      {raw && <CopyButton value={raw} />}
    </span>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Device = Record<string, any>;

interface DeviceDetailProps {
  uuid: string | null;
  device?: Device;
  deviceData: Device | null;
  api: ApiService | null;
  onRefresh: (uuid: string) => void;
  loading: boolean;
}

export const DeviceDetail = ({ uuid, device, deviceData, api, onRefresh, loading }: DeviceDetailProps) => {
  const [activeTab, setActiveTab] = useState('info');
  const [firmware, setFirmware] = useState('');
  const [actionResult, setActionResult] = useState<{ success: boolean; message: string } | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  if (!uuid) return (
    <Card title="Device Details" icon={Cpu} className="h-full">
      <p className="text-gray-500">Select a device to view details</p>
    </Card>
  );

  const hb = deviceData?.hb || deviceData?.state || device?.hb || device?.state || {};
  const info: Record<string, unknown> = deviceData?.info?.[0]?.data || {};
  const sn: string = device?.sn || (info.SN as string) || uuid.slice(0, 8);
  const cmds: Record<string, number> = deviceData?.cmds_single || {};
  const screenshotMeta = deviceData?.screenshot as { time: string; type: string } | undefined;
  const sleepFromBoot = deviceData?.sleepFromBoot as number | undefined;
  const sleep5min = deviceData?.sleep5min as number | undefined;
  const registered = deviceData?.registered as boolean | undefined;
  const hbHist: number[] = deviceData?.hb_hist || [];

  const tabs = [
    { id: 'info', label: 'Info & Commands' },
    { id: 'screenshot', label: 'Screenshot' },
    { id: 'logs', label: 'Logs' },
  ];

  const executeAction = async (action: string, actionFn: () => Promise<unknown>) => {
    setActionLoading(action);
    setActionResult(null);
    try {
      await actionFn();
      setActionResult({ success: true, message: `${action} command queued successfully` });
    } catch (e) {
      setActionResult({ success: false, message: (e as Error).message });
    }
    setActionLoading(null);
  };

  const handleRestart = () => executeAction('Restart', () => api!.restartDevice(uuid));
  const handleRequestInfo = () => executeAction('Request Info', () => api!.requestInfo(uuid));
  const handleUpdateFirmware = () => {
    if (!firmware) return;
    executeAction('Update Firmware', () => api!.updateDevice(uuid, { firmware }));
    setFirmware('');
  };

  const lastHbRaw = hb.t || device?.last_hb || deviceData?.last_hb;
  const lastHbCopy = lastHbRaw ? formatTimestamp(lastHbRaw).full : 'N/A';
  const tempF = hb.temp !== undefined ? `${(hb.temp * 9/5 + 32).toFixed(1)}°F` : null;
  const tempC = hb.temp !== undefined ? `${hb.temp}°C` : null;
  const tempDisplay = tempF ? `${tempF} (${tempC})` : 'N/A';
  const voltDisplay = hb.volt ? `${hb.volt}V${hb.current ? ` / ${hb.current}A` : ''}` : 'N/A';
  const signalDisplay = hb.ss ? `${hb.ss} dBm` : 'N/A';
  const sleepBootDisplay = sleepFromBoot !== undefined
    ? `${(sleepFromBoot * 100).toFixed(2)}%`
    : (hb.sleep && hb.awake ? `${(100 * hb.sleep / (hb.sleep + hb.awake)).toFixed(2)}%` : 'N/A');
  const uptimeDisplay = hb.t && hb.reboot ? formatDelta(hb.t, hb.reboot) : 'N/A';
  const screenshotTimeCopy = screenshotMeta ? formatTimestamp(new Date(screenshotMeta.time)).full : null;

  return (
    <div className="h-full flex flex-col bg-gray-800 rounded-lg overflow-hidden">
      <div className="p-4 border-b border-gray-700">
        <div className="flex justify-between items-center">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-white group flex items-center gap-1">
                {sn}
                <CopyButton value={sn} />
              </h2>
              {registered !== undefined && (
                <span className={`text-xs px-2 py-0.5 rounded ${registered ? 'bg-green-900 text-green-300' : 'bg-yellow-900 text-yellow-300'}`}>
                  {registered ? 'Registered' : 'Unregistered'}
                </span>
              )}
            </div>
            <p className="text-xs text-gray-400 font-mono group flex items-center gap-1">
              {uuid}
              <CopyButton value={uuid} />
            </p>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={device?.status || deviceData?.status} />
            <Button onClick={() => onRefresh(uuid)} size="sm" variant="ghost"><RefreshCw size={14} /></Button>
          </div>
        </div>
      </div>

      <div className="px-4 pt-2">
        <Tabs tabs={tabs} active={activeTab} onChange={setActiveTab} />
      </div>

      <div className="flex-1 overflow-auto p-4 pt-0">
        {activeTab === 'info' && (
          <div className="space-y-4">
            {loading ? (
              <div className="flex items-center gap-2 text-gray-400 py-4"><RefreshCw className="animate-spin" size={16} /> Loading...</div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm bg-gray-750 rounded-lg p-3">
                  <div className="flex items-center gap-2"><Clock size={14} className="text-blue-400" /><span className="text-gray-400">Last HB:</span></div>
                  <span className="group flex items-center gap-1"><TimestampDisplay value={lastHbRaw} /><CopyButton value={lastHbCopy} /></span>

                  <div className="flex items-center gap-2"><Thermometer size={14} className="text-orange-400" /><span className="text-gray-400">Temp:</span></div>
                  <Val copyValue={tempDisplay}>{tempDisplay}</Val>

                  <div className="flex items-center gap-2"><Battery size={14} className="text-green-400" /><span className="text-gray-400">Voltage:</span></div>
                  <Val copyValue={voltDisplay}>{voltDisplay}</Val>

                  <div className="flex items-center gap-2"><Signal size={14} className="text-purple-400" /><span className="text-gray-400">Signal:</span></div>
                  <Val copyValue={signalDisplay}>{signalDisplay}</Val>

                  <div className="flex items-center gap-2"><Wifi size={14} className="text-cyan-400" /><span className="text-gray-400">Connection:</span></div>
                  <Val>{hb.conn || 'N/A'}</Val>

                  <div className="flex items-center gap-2"><Package size={14} className="text-yellow-400" /><span className="text-gray-400">Firmware:</span></div>
                  <Val>{hb.jar || 'N/A'}</Val>

                  <div className="flex items-center gap-2"><Moon size={14} className="text-indigo-400" /><span className="text-gray-400">Sleep (boot):</span></div>
                  <Val copyValue={sleepBootDisplay}>{sleepBootDisplay}</Val>

                  {sleep5min !== undefined && (<>
                    <div className="flex items-center gap-2"><Moon size={14} className="text-indigo-300" /><span className="text-gray-400">Sleep (5min):</span></div>
                    <Val>{sleep5min}%</Val>
                  </>)}

                  <div className="flex items-center gap-2"><Clock size={14} className="text-teal-400" /><span className="text-gray-400">Uptime:</span></div>
                  <Val copyValue={uptimeDisplay}>{uptimeDisplay}</Val>

                  {hb.apn && (<>
                    <div className="flex items-center gap-2"><Wifi size={14} className="text-gray-400" /><span className="text-gray-400">APN:</span></div>
                    <Val>{hb.apn}</Val>
                  </>)}

                  {hb.iccid && (<>
                    <div className="flex items-center gap-2"><FileText size={14} className="text-gray-400" /><span className="text-gray-400">ICCID:</span></div>
                    <Val className="text-white font-mono text-xs">{hb.iccid}</Val>
                  </>)}

                  {hb.timezone && (<>
                    <div className="flex items-center gap-2"><Clock size={14} className="text-gray-400" /><span className="text-gray-400">Timezone:</span></div>
                    <Val copyValue={`UTC${parseFloat(hb.timezone) >= 0 ? '+' : ''}${hb.timezone}`}>
                      UTC{parseFloat(hb.timezone) >= 0 ? '+' : ''}{hb.timezone}
                    </Val>
                  </>)}
                </div>

                {screenshotMeta && (
                  <div className="p-3 bg-gray-750 rounded-lg">
                    <div className="flex items-center gap-2 text-sm group">
                      <Image size={14} className="text-gray-400" />
                      <span className="text-gray-400">Last Screenshot:</span>
                      <TimestampDisplay value={new Date(screenshotMeta.time)} />
                      <CopyButton value={screenshotTimeCopy} />
                      <span className="text-gray-500 text-xs">({screenshotMeta.type})</span>
                    </div>
                  </div>
                )}

                <PendingCommands cmds={cmds} />

                {(device?.setting || deviceData?.setting) && (
                  <Card title="Device Settings" icon={Settings} className="bg-gray-750">
                    <div className="text-sm space-y-2">
                      <div className="group">
                        <span className="text-gray-400">URL:</span>
                        <p className="text-white text-xs font-mono break-all flex items-center gap-1">
                          {device?.setting?.url || deviceData?.setting?.url || 'Not set'}
                          <CopyButton value={device?.setting?.url || deviceData?.setting?.url} />
                        </p>
                      </div>
                      <div className="flex gap-4 group">
                        <span className="text-gray-400">Rotation:
                          <span className="text-white ml-1">{device?.setting?.rotation || deviceData?.setting?.rotation || '0'}°</span>
                        </span>
                        <CopyButton value={`${device?.setting?.rotation || deviceData?.setting?.rotation || '0'}°`} />
                      </div>
                    </div>
                  </Card>
                )}

                <Card title="Device Control" icon={Terminal} className="bg-gray-750">
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      <Button size="sm" onClick={handleRestart} variant="danger" disabled={actionLoading === 'Restart'}>{actionLoading === 'Restart' ? <RefreshCw size={14} className="animate-spin" /> : <RotateCw size={14} />} Restart</Button>
                      <Button size="sm" onClick={handleRequestInfo} variant="secondary" disabled={actionLoading === 'Request Info'}>{actionLoading === 'Request Info' ? <RefreshCw size={14} className="animate-spin" /> : <Info size={14} />} Request Info</Button>
                    </div>
                    <div className="flex gap-2">
                      <Input placeholder="Firmware version (e.g. 1.7.12)" value={firmware} onChange={e => setFirmware(e.target.value)} className="flex-1" />
                      <Button variant="success" onClick={handleUpdateFirmware} disabled={!firmware || actionLoading === 'Update Firmware'}>{actionLoading === 'Update Firmware' ? <RefreshCw size={14} className="animate-spin" /> : <Package size={14} />} Update</Button>
                    </div>
                    {actionResult && (<div className={`p-2 rounded text-sm ${actionResult.success ? 'bg-green-900/50 text-green-200' : 'bg-red-900/50 text-red-200'}`}>{actionResult.message}</div>)}
                  </div>
                </Card>

                {info.SN && (
                  <Card title="System Info" icon={FileText} className="bg-gray-750">
                    <div className="text-sm space-y-1 max-h-48 overflow-auto">
                      {Object.entries(info).map(([k, v]) => {
                        const display = typeof v === 'object' ? JSON.stringify(v).slice(0, 60) + '...' : String(v);
                        return (
                          <div key={k} className="flex justify-between py-1 border-b border-gray-700 last:border-0 group">
                            <span className="text-gray-400">{k}</span>
                            <span className="text-white truncate ml-2 text-right flex items-center gap-1" style={{ maxWidth: '65%' }}>
                              <span className="truncate">{display}</span>
                              <CopyButton value={typeof v === 'object' ? JSON.stringify(v) : String(v)} />
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </Card>
                )}

                {hbHist.length > 0 && (
                  <Card title="Recent Heartbeats" icon={Clock} className="bg-gray-750">
                    <div className="flex flex-wrap gap-2">
                      {hbHist.slice(0, 10).map((ts, i) => (
                        <span key={i} className="text-xs bg-gray-700 px-2 py-1 rounded text-gray-300 group flex items-center gap-1">
                          {new Date(ts).toLocaleTimeString()}
                          <CopyButton value={formatTimestamp(ts).full} size={10} />
                        </span>
                      ))}
                    </div>
                    {hbHist.length > 1 && (
                      <p className="text-xs text-gray-500 mt-2">
                        Avg interval: {((hbHist[0] - hbHist[hbHist.length - 1]) / (hbHist.length - 1) / 1000).toFixed(0)}s
                      </p>
                    )}
                  </Card>
                )}
              </>
            )}
          </div>
        )}
        {activeTab === 'screenshot' && <ScreenshotPanel api={api} uuid={uuid} sn={sn} />}
        {activeTab === 'logs' && <LogsPanel api={api} uuid={uuid} sn={sn} />}
      </div>
    </div>
  );
};
